import { EmblaOptionsType } from 'embla-carousel';
import React, { useRef, useState } from 'react';

import { useSiteContext } from '@zooplus/commons-lang';
import { Cart } from '@zooplus/icons';
import { ChipGroup, SelectableChip } from '@zooplus/ui-components';
import { RecommendationsCarousel } from '@zooplus/recommendations-component';

import { ENV } from '../../../environment';
import { useTranslation } from '../../../hooks/use-translations';
import useMedias from '../../../hooks/useMedias';
//import { ProductInfo } from '../../../model/product';
import { Link } from '../../common/link/Link';
import PetsFamily from '../../pets-family/PetsFamily';
import catStyles from './CatRecommendations.module.scss';
import styles from '../OwnBrandRecommendations.module.scss';
import '@zooplus/recommendations-component/dist/es/shsvs-recommendations-component.min.css';
import { useCartContext } from '@zooplus/cart-provider';
//import { SetArticleQuantityBody } from '@zooplus/checkout-state';

export type CatRecommendationsProps = {
  productsBySlider: Record<string, any | undefined>;
};

export const CatRecommendations = ({ productsBySlider }: CatRecommendationsProps) => {
  const [categorySelected, setCategorySelected] = useState('dry-food-cats');
  const sliderRefs = useRef<Record<string, HTMLElement | null>>({});
  const { isMobile, isSmallMobile } = useMedias();
  const imageSize = isMobile || isSmallMobile ? '500x80' : '2880x280';
  const imageSizeCatDryFood = isMobile || isSmallMobile ? '500x80' : '2880x480';
  const { site, language } = useSiteContext();
  const { t } = useTranslation();
  const options: EmblaOptionsType = {
    align: 'center',
    breakpoints: {
      '(min-width: 0px)': {
        align: 'center',
        slidesToScroll: 2,
      }, 
      '(min-width: 544px)': {
        align: 'start',
        slidesToScroll: 2,
      },
      '(min-width: 686px)': {
        align: 'start',
        slidesToScroll: 3,
      },
      '(min-width: 844px)': {
        slidesToScroll: 4,
      },
      '(min-width: 1280px)': {
        slidesToScroll: 5,
      },
      '(min-width: 1920px)': {
        slidesToScroll: 5,
      },
    },
    slidesToScroll: 1,
  };

  let suffix = language.toUpperCase();
  if (suffix == 'EN') {
    suffix = 'UK';
  }

  if (site.domain == 'zooplus.ch' && suffix == 'DE') {
    suffix = 'CH';
  }
  if (site.domain == 'zoohit.cz' && suffix == 'CS') {
    suffix = 'CZ';
  }
  if (site.domain == 'zoohit.si' && suffix == 'SL') {
    suffix = 'SI';
  }
  if (site.domain == 'zooplus.no' && suffix == 'NB') {
    suffix = 'NO';
  }
  if (site.domain == 'zooplus.gr' && suffix == 'EL') {
    suffix = 'GR';
  }
  if (site.domain == 'zooplus.dk' && suffix == 'DA') {
    suffix = 'DK';
  }

  const recoSliders = [
    {
      id: 'dry-food-cats',
      titleBig: 'Dry Food',
      titleSmall: 'for cats',
      chipLabel: t('ob.product_reco.cat.dry.label'),
      moreProductsLink: t('ob.product_reco.cat.dry.recolink'),
      icon: <Cart />,
      bannerImage: `${ENV.IMAGE_CDN}/product-recommendations/CategoryHeaders_C_${suffix}/OwnBrands_Umbrella_LP_Dry_Category_${suffix}/OwnBrands_Umbrella_LP_Dry_C_Category_${imageSizeCatDryFood}_${suffix}.jpg`,
      name: 'container_1',
    },
    {
      id: 'wet-food-cats',
      titleBig: 'Wet food',
      titleSmall: 'for cats',
      chipLabel: t('ob.product_reco.cat.wet.label'),
      moreProductsLink: t('ob.product_reco.cat.wet.recolink'),
      icon: <Cart />,
      bannerImage: `${ENV.IMAGE_CDN}/product-recommendations/CategoryHeaders_C_${suffix}/OwnBrands_Umbrella_LP_Wet_Category_${suffix}/OwnBrands_Umbrella_LP_Wet_C_Category_${imageSize}_${suffix}.jpg`,
      name: 'container_2',
    },
    {
      id: 'litter-cats',
      titleBig: 'Litter',
      titleSmall: 'for cats',
      chipLabel: t('ob.product_reco.cat.litter.label'),
      moreProductsLink: t('ob.product_reco.cat.litter.recolink'),
      icon: <Cart />,
      bannerImage: `${ENV.IMAGE_CDN}/product-recommendations/CategoryHeaders_C_${suffix}/OwnBrands_Umbrella_LP_Litter_Category_${suffix}/OwnBrands_Umbrella_LP_Litter_C_Category_${imageSize}_${suffix}.jpg`,
      name: 'container_3',
    },
    {
      id: 'snacks-cats',
      titleBig: 'Snacks',
      titleSmall: 'for cats',
      chipLabel: t('ob.product_reco.cat.snacks.label'),
      moreProductsLink: t('ob.product_reco.cat.snacks.recolink'),
      icon: <Cart />,
      bannerImage: `${ENV.IMAGE_CDN}/product-recommendations/CategoryHeaders_C_${suffix}/OwnBrands_Umbrella_LP_Snacks_Category_${suffix}/OwnBrands_Umbrella_LP_Snacks_C_Category_${imageSize}_${suffix}.jpg`,
      name: 'container_4',
    },
  ];

  const handleChipClick = (sliderId: string) => {
    setCategorySelected(sliderId);
    const sliderElement = sliderRefs.current[sliderId];
    if (sliderElement) {
      sliderElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const { changeArticleQuantity } = useCartContext();
  const [addingToCart, setAddingToCart] = useState(false);


  function addToCart(body) {
    setAddingToCart(true);
    changeArticleQuantity({
      articleId: body.articleId,
      quantity: 1,
      subscriptionSelected: false,
      popover: body.popover.trigger ? { trigger: body.popover.trigger } : undefined, 
    }).then((_) => setAddingToCart(false));
  }

  return (
    <section id="recommendations"  className={`${styles.recommendationsWrapper} ${catStyles.recommendationsWrapper}`}>
      {/* <div role="tablist" aria-orientation="horizontal" className={styles.tablist}>
            <button aria-controls="panel-own-brand-recommendations-0" aria-selected="true" className={styles.tabsTab} data-zta="tabsTab" id="tab-own-brand-recommendations-0" role="tab" >Katzen</button>
            <button aria-controls="panel-own-brand-recommendations-1" aria-selected="false" className={styles.tabsTab}  data-zta="tabsTab" id="tab-own-brand-recommendations-1" role="tab" >Hunde</button>
      </div> */}

      <ChipGroup className="pp-mb-m">
        {recoSliders.map((recoSlider) => {
          const sliderProducts = productsBySlider[recoSlider.name];
          return sliderProducts && sliderProducts.length > 4 ? (
            <SelectableChip
              key={`own-brand-recommendations-chip-category-${recoSlider.id}`}
              inputType="radio"
              inputName="own-brand-recommendations-cat"
              label={recoSlider.chipLabel}
              inputId={`own-brand-recommendations-category-${recoSlider.id}`}
              onClick={() => handleChipClick(recoSlider.id)}
              icon={recoSlider.icon}
              selected={recoSlider.id === categorySelected}
            />
          ) : null;
        })}
      </ChipGroup>

      {recoSliders.map((recoSlider, index) => {
        const sliderProducts = productsBySlider[recoSlider.name];
        // const recommendations = sliderProducts
        //   ? sliderProducts.map((product, idx) => ({
        //       siteId: site.id,
        //       language: language.toUpperCase(),
        //       locale: site.locale,
        //       article: {
        //         bonuspoints: null,
        //         id: product.articleShopId,
        //         name: product.variantName || product.productName,
        //         unit: 'kg',
        //         variantId: Number(product.productShopId),
        //       },
        //       articleOffers: [
        //         {
        //           id: `1p-${product.productShopId}`,
        //           type: "1P",
        //           discount: {
        //             type: 'PERCENTAGE_DISCOUNT',
        //             value: 20,
        //           },
        //           price: {
        //             currency: 'EUR',
        //             label: product.originalPriceFormatted,
        //             labelFull: product.originalPriceFormatted,
        //             labelPattern: 'bei uns {0}',
        //             labelPrefix: 'bei uns',
        //             value: product.originalPrice,
        //           },
        //           unitPrice: {
        //             currency: 'EUR',
        //             label: product.unitPriceFormatted,
        //             labelFull: product.unitPriceFormatted,
        //             labelPattern: '{0}',
        //             labelPrefix: '',
        //             value: product.unitPrice,
        //           },
        //         },
        //       ],
        //       product: {
        //         badges: {
        //           sponsored: false,
        //           zooplusChoice: {
        //             active: false,
        //             description: 'Kundenempfehlungen mit hervorragendem Preis-Leistungs-Verhältnis.',
        //             label: 'Unser Favorit',
        //           },
        //         },
        //         feedbackAggregate: {
        //           rating: product.rating,
        //           reviewCount: product.votes || 0,
        //         },
        //         link: product.pdpUrl,
        //         name: product.productName,
        //         pictureUrl: product.pictureUrl,
        //         shopIdentifier: Number(product.productShopId),
        //       },
        //       trackingId: `own-brand-${(parseInt(product.articleShopId.toString(), 10) * 23 + 7).toString(36).slice(-6)}-${(parseInt(product.productShopId.toString(), 10) * 23 + 7).toString(36).slice(-6)}`,
        //       weight: 0,
        //     }))
        //   : [];
       // console.log(recommendations);

        if (sliderProducts && sliderProducts.length > 4) {
          return (
            <React.Fragment key={recoSlider.id}>
              <div ref={(el) => (sliderRefs.current[recoSlider.id] = el)}>
                <div className={styles.recommendationSectionHeader}>
                  <a href={recoSlider.moreProductsLink}>
                    <img className={styles.carouseBanner} src={recoSlider.bannerImage} alt="" />
                  </a>
                </div>
                <div className={styles.showMoreProductsLink}>
                  <Link href={recoSlider.moreProductsLink} textKey={'ob.product_reco.label'} />
                </div>
                <RecommendationsCarousel
                  caption=""
                  priceVersion="2024"
                  addToCartButton={{
                    loadingIndicator: addingToCart,
                    onAddToCartClick: (useCartContext ) => {
                      addToCart?.(  useCartContext);
                    },
                    setRestoreArticle: () => {},
                    showPopover: true,

                  }}
                  className={styles.carousel}
                  key={`${recoSlider.id}`}
                  id={`own-brand-recommendations-products-${recoSlider.id}`}
                  emblaOptions={options}
                 recommendations={sliderProducts}
                />

              </div>
              <div className={catStyles.tempPopUp}></div>
            </React.Fragment>
          );
        } else {
          return null;
        }
      })}
      <PetsFamily type="cats" />
    </section>
  );
};
